import { Toaster as Sonner, type ToasterProps as SonnerProps } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2, X } from "lucide-react";

type ToasterProps = SonnerProps;

// Site design-system tokens only (src/styles.css): --radius-lg, --shadow-card, and the
// success/destructive/warning/primary color families. No richColors/built-in theme — explicit
// per-part classNames give exact control over the brand palette instead of Sonner's generic ones.
const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      className="toaster group"
      position="bottom-right"
      // Lifted clear of the fixed "Go to top" control, which shares this corner. Only the bottom
      // edge moves; a toast with nothing below it still reads as bottom-right.
      offset={{ bottom: "6.5rem" }}
      mobileOffset={{ bottom: "6.5rem" }}
      duration={4500}
      closeButton
      icons={{
        success: <CheckCircle2 className="h-5 w-5 text-success" />,
        error: <XCircle className="h-5 w-5 text-destructive" />,
        warning: <AlertTriangle className="h-5 w-5 text-warning" />,
        info: <Info className="h-5 w-5 text-primary" />,
        loading: <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />,
        close: <X className="h-4 w-4" />,
      }}
      toastOptions={{
        unstyled: false,
        classNames: {
          toast:
            "group toast group-[.toaster]:rounded-lg group-[.toaster]:border group-[.toaster]:bg-card " +
            "group-[.toaster]:text-card-foreground group-[.toaster]:shadow-[var(--shadow-card)] " +
            "group-[.toaster]:font-medium group-[.toaster]:gap-3 group-[.toaster]:p-4",
          title: "group-[.toast]:text-sm group-[.toast]:font-semibold group-[.toast]:leading-snug",
          description:
            "group-[.toast]:text-xs group-[.toast]:text-muted-foreground group-[.toast]:mt-0.5",
          icon: "group-[.toast]:shrink-0",
          closeButton:
            "group-[.toast]:absolute group-[.toast]:right-2 group-[.toast]:top-2 group-[.toast]:left-auto " +
            "group-[.toast]:translate-x-0 group-[.toast]:translate-y-0 group-[.toast]:rounded-md " +
            "group-[.toast]:border-none group-[.toast]:bg-transparent group-[.toast]:text-muted-foreground " +
            "group-[.toast]:opacity-70 hover:group-[.toast]:opacity-100 hover:group-[.toast]:bg-muted " +
            "group-[.toast]:transition-opacity",
          actionButton:
            "group-[.toast]:rounded-md group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:rounded-md group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          success:
            "group-[.toaster]:bg-success-muted group-[.toaster]:text-success group-[.toaster]:border-success/30",
          error:
            "group-[.toaster]:bg-destructive-muted group-[.toaster]:text-destructive group-[.toaster]:border-destructive/30",
          warning:
            "group-[.toaster]:bg-warning-muted group-[.toaster]:text-warning group-[.toaster]:border-warning/30",
          info: "group-[.toaster]:bg-muted group-[.toaster]:text-foreground group-[.toaster]:border-primary/20",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
